/* eslint-disable max-len */
import {
    CloudPartner,
    Office,
    Saas,
    AuthorizedLocation,
    Status,
    AgreementType,
    DataTransferAuthorization,
    DataTransferAuthorizationTemplate,
    Entity,
} from '@data/models/gdpr.model';

export const fullDataTransferAuthorized: Record<AuthorizedLocation, DataTransferAuthorizationTemplate> = {
    [AuthorizedLocation.FRANCE]: {
        authorizedLocation: AuthorizedLocation.FRANCE,
        agreementType: AgreementType.PRIOR_AGREEMENT,
        text: 'France',
        allowedCountry: ['FR'],
        hasAgreementType: false,
        isFr: true,
    },
    [AuthorizedLocation.ALL_EU]: {
        authorizedLocation: AuthorizedLocation.ALL_EU,
        agreementType: AgreementType.UNDECLARED,
        text: 'UE',
        allowedCountry: ['IT', 'PT', 'EU'],
        tooltip: 'Inclu les pays qui ne sont pas membres de l\'UE mais inclus dans l\'EEE (Norvège, Liechtenstein, Islande)',
        hasAgreementType: true,
        isFr: false,
    },
    [AuthorizedLocation.ADEQUATE_COUNTRIES_ONLY]: {
        authorizedLocation: AuthorizedLocation.ADEQUATE_COUNTRIES_ONLY,
        agreementType: AgreementType.UNDECLARED,
        text: 'Pays adéquats',
        allowedCountry: ['GB'],
        tooltip: 'La liste des pays adéquats est disponible ici : cnil.fr/fr/la-protection-des-donnees-dans-le-monde. Le Royaume-Uni fait partie des pays adéquats vis-à-vis du RGPD.',
        hasAgreementType: true,
        isFr: false,
    },
    [AuthorizedLocation.ALL_WORLD]: {
        authorizedLocation: AuthorizedLocation.ALL_WORLD,
        agreementType: AgreementType.UNDECLARED,
        text: 'Monde',
        allowedCountry: ['IN', 'US', 'BR'],
        hasAgreementType: true,
        isFr: false,
    },
    [AuthorizedLocation.UNDECLARED]: {
        authorizedLocation: AuthorizedLocation.UNDECLARED,
        agreementType: AgreementType.UNDECLARED,
        text: 'Non renseigné',
        allowedCountry: [],
        hasAgreementType: false,
        isFr: false,
    },
};

export const fullStatus = {
    [Status.YES]: {
        text: 'Oui, possible',
        value: Status.YES,
        hasAgreementType: true,
    },
    [Status.NO]: {
        text: 'Non, pas possible',
        value: Status.NO,
        hasAgreementType: false,
    },
    [Status.UNDECLARED]: {
        text: 'Non renseigné',
        value: Status.UNDECLARED,
        hasAgreementType: false,
    }
};

export const fullAgreementType = {
    [AgreementType.PRIOR_AGREEMENT]: {
        value: AgreementType.PRIOR_AGREEMENT,
        text: 'Accord préalable',
        tooltip: 'L\'ajout ou le remplacement d\'un sous-traitant doit être accepté par le client avant tout changement. L\'accord du client doit être demandé au moins 30 jours avant le changement car le client dispose de 30 jours pour s\'opposer au changement. Le changement ne peut avoir lieu qu\'après l\'accord du client',
    },
    [AgreementType.PRIOR_INFORMATION]: {
        value: AgreementType.PRIOR_INFORMATION,
        text: 'Information préalable',
        tooltip: 'L\'ajout ou le remplacement d\'un sous-traitant doit être communiqué au client au moins 30 jours avant - L\'information est envoyée automatiquement au moment de la mise à jour de l\'espace RGPD Synapps',
    },
    [AgreementType.UNDECLARED]: {
        value: AgreementType.UNDECLARED,
        text: 'Non renseigné',
        tooltip: '',
    },
};

export const LEGAL_GUARANTEES = {
    NA: 'Non applicable, pas de transfert hors UE',
    EU: 'Clauses Contractuelles Types de la Commission Européenne',
    OK: 'Non applicable, transfert vers un pays adéquat',
    // eslint-disable-next-line max-len
    TBD: 'Garantie juridique à définir et mettre en œuvre par le Client (par exemple le privacy shield pour les USA, les clauses contractuelles types de la Commission Européenne à signer entre le responsable de traitement et le cloud provider)',
} satisfies { [key: string]: string };

export const OFFICES = {
    CLARANET: {
        name: 'Claranet SAS',
        address: '2 rue des Landelles - 35510 Cesson-Sévigné',
        countryCodes: ['FR']
    },
    CLARANET_UK: {
        name: 'Claranet GROUP Limited',
        address: '110 High Holborn, London, England WC1V 6JS',
        countryCodes: ['GB']
    },
    CLARANET_IN: {
        name: 'Claranet IND',
        address: 'India',
        countryCodes: ['IN']
    },
    CLARANET_IT: {
        name: 'Claranet IT',
        address: 'Italy',
        countryCodes: ['IT']
    },
    CLARANET_PT: {
        name: 'Claranet PT',
        address: 'Portugal',
        countryCodes: ['PT']
    },
    CLARANET_BR: {
        name: 'Claranet BR',
        address: 'Brésil',
        countryCodes: ['BR']
    },
} satisfies { [key: string]: Office };

export const LEGAL_GUARANTEES_DATACENTER = {
    PRIVACY_SHIELD: 'Clauses Contractuelles Types',
} satisfies { [key: string]: string };

export const DATACENTERS = {
    EQUINIX_PA1: {
        name: 'Equinix PA1',
        address: '167 Rue de la Belle Etoile, 95948 Roissy-en-France',
        countryCodes: ['FR'],
    },
    EQUINIX_PA2: {
        name: 'Equinix PA2 / PA3',
        address: '114 Rue Ambroise Croizat, 93200 Saint Denis',
        countryCodes: ['FR'],
    },
    EQUINIX_PA4: {
        name: 'Equinix PA4',
        address: '110 bis Avenue du Général Leclerc, 93500 Pantin',
        countryCodes: ['FR'],
    },
    EQUINIX_PA6: {
        name: 'Equinix PA6',
        address: '10 Rue Waldeck Rochet, Immeuble 520, 93300 Aubervilliers',
        countryCodes: ['FR'],
    },
    EQUINIX_PA7: {
        name: 'Equinix PA7',
        address: 'Energy Park - Bâtiment 9, 130-136 Boulevard de Verdun, 92413 Courbevoie',
        countryCodes: ['FR'],
    },
    IBM_CLICHY: {
        name: 'IBM Clichy',
        address: '7-9 rue Petit, 92110 Clichy',
        countryCodes: ['FR'],
    },
    INTERXION_PAR5: {
        name: 'INTERXION PAR-5',
        address: '11-13 Avenue Arts et Métiers, 93200 Saint Denis',
        countryCodes: ['FR'],
    },
    TELEHOUSE_TH2: {
        name: 'TELEHOUSE TH2',
        address: '137 Boulevard Voltaire, 75011 Paris',
        countryCodes: ['FR'],
    },
    TELEHOUSE_TH3: {
        name: 'TELEHOUSE TH3',
        address: '1 rue Pablo Picasso, 78114 Magny-Les-Hameaux',
        countryCodes: ['FR'],
    },
    IRON_MOUNTAIN_FRANCE: {
        name: 'Iron Mountain France',
        address: 'Zone Industrielle Les Sables – 6-12, avenue Descartes – 91420 Morangis',
        countryCodes: ['FR'],
    },
    ETIX_EVERYWHERE: {
        name: 'Etix Everywhere Lille #3 (ex CIV ADC2)',
        address: 'ZAC du Parc des rives de l’Escaut - 486 Avenue Augusta Ada King, 59410 Anzin',
        countryCodes: ['FR'],
    },
    ETIX_EVERYWHERE_LILLE_2: {
        name: 'Etix Everywhere Lille #2 (ex CIV ADC1)',
        address: 'Rue des Saules, 59262 Sainghin-en-Mélantois',
        countryCodes: ['FR'],
    }
} satisfies { [key: string]: Entity };

// For Cloud Partners, countryCodes are legacy. We put a false XX value
export const CLOUD_PARTNERS: { [key: string]: CloudPartner } = {
    OVH: {
        name: 'OVH',
        address: '2 rue Kellerman, 59100 ROUBAIX',
        countryCodes: ['XX'],
        legalGuarantees: [
            LEGAL_GUARANTEES.EU
        ]
    },
    AWS: {
        name: 'AWS',
        address: '410 Terry Avenue North, Seattle, Washington 98109-5210, U.S.A',
        countryCodes: ['XX'],
        legalGuarantees: [
            LEGAL_GUARANTEES.EU
        ]
    },
    GOOGLE: {
        name: 'GCP (Google Cloud Platform)',
        address: 'Gordon House, Barrow Street, Dublin 4, Ireland',
        countryCodes: ['XX'],
        legalGuarantees: [
            LEGAL_GUARANTEES.EU
        ]
    },
    MICROSOFT: {
        name: 'Microsoft AZURE',
        // eslint-disable-next-line max-len
        address: 'Microsoft Ireland Operations Limited, Atrium Building Block A, Carmen Hall Road, Sandyford Industrial Park, Dublin, 18, Ireland',
        countryCodes: ['XX'],
        legalGuarantees: [
            LEGAL_GUARANTEES.EU
        ]
    },
    MICROSOFT_OFFICE_365: {
        name: 'Microsoft Office 365',
        // eslint-disable-next-line max-len
        address: 'Microsoft Ireland Operations Limited, Atrium Building Block A, Carmen Hall Road, Sandyford Industrial Park, Dublin, 18, Ireland',
        countryCodes: ['XX'],
        legalGuarantees: [
            LEGAL_GUARANTEES.EU
        ]
    }
};

export const CLOUD_PARTNERS_LOCATION_LINK: { [key: string]: string } = {
    'OVH': 'https://www.ovhcloud.com/fr/datacenters-ovhcloud/#text-media-2-1',
    'AWS': 'https://aws.amazon.com/fr/about-aws/global-infrastructure/regions_az/',
    'GCP (Google Cloud Platform)': 'https://cloud.google.com/about/locations',
    'Microsoft AZURE': 'https://azure.microsoft.com/en-us/global-infrastructure/geographies/',
    'Microsoft Office 365': 'https://azure.microsoft.com/en-us/global-infrastructure/geographies/'
};

export const CLOUD_PARTNERS_COUNTRIES: { [key: string]: { countryCodes: string[] } } = {
    'OVH': {
        countryCodes: ['FR', 'CA']
    },
    'AWS': {
        countryCodes: ['FR', 'US', 'JP', 'KR', 'IN', 'SG', 'AU', 'TW', 'CA', 'CN', 'DE', 'IE', 'GB', 'BR', 'NL']
    },
    'GCP (Google Cloud Platform)': {
        countryCodes: ['FR', 'US', 'JP', 'KR', 'IN', 'SG', 'AU', 'TW', 'CA', 'CN', 'DE', 'IE', 'GB', 'BR', 'NL']
    },
    'Microsoft AZURE': {
        countryCodes: ['FR', 'US', 'JP', 'KR', 'IN', 'SG', 'AU', 'TW', 'CA', 'CN', 'DE', 'IE', 'GB', 'BR', 'NL']
    },
    'Microsoft Office 365': {
        countryCodes: ['FR', 'US', 'JP', 'KR', 'IN', 'SG', 'AU', 'TW', 'CA', 'CN', 'DE', 'IE', 'GB', 'BR', 'NL']
    }
};
export const CLOUD_PARTNERS_LEGAL_GUARANTEES: { [key: string]: { legaleGuaranteesLink?: string } } = {
    OVH: {},
    AWS: {
        legaleGuaranteesLink: 'https://d1.awsstatic.com/legal/aws-gdpr/AWS_GDPR_DPA.pdf',
    },
    'GCP (Google Cloud Platform)': {
        legaleGuaranteesLink: 'https://cloud.google.com/terms/eu-model-contract-clause',
    },
    'Microsoft AZURE': {
        legaleGuaranteesLink: 'https://www.microsoftvolumelicensing.com/DocumentSearch.aspx?Mode=2&Keyword=DPA',
    },
    'Microsoft Office 365': {
        legaleGuaranteesLink: 'https://www.microsoftvolumelicensing.com/DocumentSearch.aspx?Mode=2&Keyword=DPA',
    }
};

export const LEGAL_GUARANTEES_SAAS = {
    DEFAULT: 'No legal guarantee',
    PRIVACY_SHIELD: 'Clauses Contractuelles Types',
    EU_MODEL_CLAUSE: 'Clauses Contractuelles Types de la Commission Européenne'
} satisfies { [key: string]: string };

export const CLARANET_SOLUTION = 'Claranet';

export const OFFER_WITH_LINK_REFERENCE_COUNTRY = 'US';

export const SAAS_TOOLS: { [key: string]: Saas } = {
    WADP: {
        name: 'WADP',
        description: 'Fonctionnalités de CDN (Content Delivery Network) et WAF (Web Application Firewall)',
        icon: '/assets/img/service-offers/claranet.png',
        solution: CLARANET_SOLUTION,
        countryCodes: [
            'GB', 'IN'
        ],
        address: 'Claranet GROUP Limited, 110 High Holborn, London, England WC1V 6JS',
    },
    AVEPOINT: {
        name: 'AvePoint',
        description: 'Solution SaaS de sauvegarde d\'environnements 365',
        icon: '/assets/img/service-offers/avepoint.png',
        solution: 'AvePoint',
        countryCodes: [
            'US'
        ],
        address: '525 Washington Blvd, Suite 1400 Jersey City, NJ 07310',
    },
    OH_MY_LOG: {
        name: 'OhMyLog',
        description: 'Plateforme SaaS pour simplifier l\'exploitation des logs',
        icon: '/assets/img/service-offers/oh-my-log.png',
        solution: 'Microsoft',
        countryCodes: [
            'EU'
        ],
        address: 'Microsoft Ireland Operations Limited, Atrium Building Block A, Carmen Hall Road, Sandyford Industrial Park, Dublin, 18, Ireland',
    },
    DATADOG: {
        name: 'Datadog',
        description: 'Solution de monitoring SaaS',
        icon: '/assets/img/service-offers/datadog.png',
        solution: 'Datadog',
        countryCodes: [
            'EU',
            'US'
        ],
        address: '620 8th Ave 45th Floor, New York, NY 10018 USA',
    },
    SENTINEL_ONE: {
        name: 'SentinelOne',
        description: 'Solution EDR',
        icon: '/assets/img/service-offers/sentinel-one.png',
        solution: 'SentinelOne',
        countryCodes: [
            'EU',
            'US'
        ],
        address: '444 Castro Street, Suite 400, Mountain View, CA 94041 USA',
    },
    WEBFENCE_HYBRIDE: {
        name: 'Webfence@OnPrem',
        description: 'Solution de protection des applications WEB & API hébergée OnPremise (hors centralisation des logs sur Azure)',
        icon: '/assets/img/service-offers/webfence.png',
        solution: 'Webfence',
        countryCodes: [
            'https://learn.microsoft.com/fr-fr/azure/reliability/availability-zones-service-support#azure-regions-with-availability-zone-support'
        ],
        address: 'Equinix PA2, 114 rue Ambroise Croizat, 93200 Saint Denis / Microsoft Ireland Operations Limited, Atrium Building Block A, Carmen Hall Road, Sandyford Industrial Park, Dublin, 18, Ireland',
    },
    WEBFENCE_SAAS_AZURE: {
        name: 'Webfence@SaaS',
        description: 'Solution de protection des applications WEB & API hébergée sur AWS (avec centralisation des logs sur Azure).',
        icon: '/assets/img/service-offers/webfence-claranet-x-aws.png',
        solution: 'Webfence',
        countryCodes: [
            'https://aws.amazon.com/fr/about-aws/global-infrastructure/regions_az/',
            'https://learn.microsoft.com/fr-fr/azure/reliability/availability-zones-service-support#azure-regions-with-availability-zone-support',
        ],
        address: 'Amazon Web Services, 410 Terry Avenue North, Seattle, Washington 98109-5210, U.S.A - Microsoft Ireland Operations Limited, Atrium Building Block A, Carmen Hall Road, Sandyford Industrial Park, Dublin, 18, Ireland',
    },
    BIT_TITAN: {
        name: 'BitTitan',
        description: 'Solution SaaS de migration de mailbox, OneDrive, SharePoint et Teams',
        icon: '/assets/img/service-offers/bittitan.png',
        solution: 'BitTitan',
        countryCodes: [
            'US'
        ],
        address: '10801 N Mopac Expressway Building 1, Suite 100 Austin, TX, 78759 U.S.A.',
    },
    IMPERVA: {
        name: 'IMPERVA (Solution de WAF SaaS)',
        description: 'Solution de sécurité web utilisant une combinaison de signatures de sécurité, règles personnalisables et apprentissage automatique pour bloquer les attaques en ligne.',
        icon: '/assets/img/service-offers/imperva.png',
        solution: 'Imperva',
        countryCodes: [
            'US', 'EU'
        ],
        address: 'Arboretum Plaza II, Suite 400 9442 Capital of Texas Highway North, Austin, TX 78759 USA',
    },
    CLOUD_BACKUP: {
        name: 'Cloud BackUp',
        description: 'Sauvegarde d\'environnements 365 via Skykick',
        icon: '/assets/img/service-offers/claranet.png',
        solution: 'CloudBackup',
        countryCodes: [
            'GB'
        ],
        address: 'Claranet GROUP Limited, 110 High Holborn, London, England WC1V 6JS',
    },
    MCO_IBM_POWER_AIX: {
        name: 'Heliaq Solutions - MCO IBM Power AIX',
        description: 'Prestations de MCO de serveurs IBM Power AIX',
        icon: '/assets/img/service-offers/heliaq.png',
        solution: 'mcoIbmPowerAix',
        countryCodes: [
            'FR'
        ],
        address: 'Heliaq Solutions - 3 rue Adrienne Bolland, 33185 Le Haillan',
    },
    MCO_HP_UX: {
        name: 'MCO - HP UX',
        description: 'Prestations de MCO de serveurs HP UX',
        icon: '/assets/img/service-offers/claranet.png',
        solution: 'mcoHpUx',
        countryCodes: [
            'FR'
        ],
        address: 'Jiliti - Parc Tertiaire SILIC - Bât. Bali - 100, rue des Solets - 94150 Rungis',
    },
    HORNET_SECURITY: {
        name: 'HornetSecurity',
        description: 'Solution de cybersécurité antispam et autres solutions de protection des environnements 365 et OnPrem',
        icon: '/assets/img/service-offers/hornet-security.jpg',
        solution: 'hornetSecurity',
        countryCodes: [
            'FR', 'EU'
        ],
        address: '2bis avenue Antoine Pinay, 59510 Hem',
    },
    HARFANG_LAB: {
        name: 'HarfangLab',
        description: 'Solution d\'EDR',
        icon: '/assets/img/service-offers/harfanglab.png',
        solution: 'harfangLab',
        countryCodes: [
            'FR', 'EU'
        ],
        address: 'Tour Campus Cyber, 5-7 rue Bellini, 92800 Puteaux'
    },
    SEKOIA: {
        name: 'Sekoia',
        description: 'Plateforme SIEM + CTI',
        icon: '/assets/img/service-offers/sekoia.jpg',
        solution: 'sekoia',
        countryCodes: [
            'FR'
        ],
        address: '28 boulevard du Colombier, 35000 Rennes, France'
    }
};

export const GDPR_ENTITIES_PRIMARY_FIELDS = {
    OFFICES: ['address'] as (keyof Office)[],
    DATACENTERS: ['address'] as (keyof Entity)[],
    SAAS: ['name', 'countryCodes'] as (keyof Saas)[],
    CLOUD_PARTNERS: ['name'] as (keyof CloudPartner)[],
    DATA_TRANSFER_AUTHORIZATION: ['authorizedLocation'] as (keyof DataTransferAuthorization)[],
};

export interface DefaultGdpr {
    offices?: Office[];
    datacenters?: Entity[];
    cloudPartners?: CloudPartner[];
    saas?: Saas[];
    dataTransferAuthorizations?: DataTransferAuthorization[];
}

export const DEFAULT_VALUES = {
    'template_rgpd:webapps': {
        offices: [
            OFFICES.CLARANET
        ],
        datacenters: [
            DATACENTERS.TELEHOUSE_TH3,
            DATACENTERS.EQUINIX_PA2,
            DATACENTERS.EQUINIX_PA7,
            DATACENTERS.ETIX_EVERYWHERE
        ],
        cloudPartners: [],
        saas: [],
        dataTransferAuthorizations: [fullDataTransferAuthorized.FRANCE_ONLY],
    },
    'template_rgpd:cba': {
        offices: [
            OFFICES.CLARANET
        ],
        datacenters: [
            DATACENTERS.EQUINIX_PA6,
            DATACENTERS.EQUINIX_PA7,
            DATACENTERS.IBM_CLICHY,
            DATACENTERS.ETIX_EVERYWHERE
        ],
        cloudPartners: [],
        saas: [],
        dataTransferAuthorizations: [fullDataTransferAuthorized.FRANCE_ONLY],
    },
    'template_rgpd:data': {
        offices: [
            OFFICES.CLARANET
        ],
        datacenters: [
            DATACENTERS.EQUINIX_PA2,
            DATACENTERS.INTERXION_PAR5,
            DATACENTERS.ETIX_EVERYWHERE
        ],
        cloudPartners: [],
        saas: [],
        dataTransferAuthorizations: [fullDataTransferAuthorized.FRANCE_ONLY],
    },
    'template_rgpd:esante': {
        offices: [
            OFFICES.CLARANET
        ],
        datacenters: [
            DATACENTERS.TELEHOUSE_TH3,
            DATACENTERS.EQUINIX_PA2,
            DATACENTERS.EQUINIX_PA7,
            DATACENTERS.ETIX_EVERYWHERE
        ],
        cloudPartners: [],
        saas: [],
        dataTransferAuthorizations: [fullDataTransferAuthorized.FRANCE_ONLY],
    },
    'template_rgpd:default': {
        offices: [
            OFFICES.CLARANET
        ],
        datacenters: [
            DATACENTERS.ETIX_EVERYWHERE
        ],
        cloudPartners: [],
        saas: [],
        dataTransferAuthorizations: [fullDataTransferAuthorized.FRANCE_ONLY],
    }
} satisfies { [key: string]: DefaultGdpr };

export interface CountryInfo {
    code: string;
    name: string;
    legalGuarantees: string;
    legalGuaranteesSaas: string;
}

/* eslint-disable max-len */
export const COUNTRIES: { [key: string]: CountryInfo } = {
    AF: { code: 'AF', name: 'Afghanistan', legalGuarantees: LEGAL_GUARANTEES.TBD, legalGuaranteesSaas: LEGAL_GUARANTEES_SAAS.DEFAULT },
    ZA: { code: 'ZA', name: 'Afrique Du Sud', legalGuarantees: LEGAL_GUARANTEES.TBD, legalGuaranteesSaas: LEGAL_GUARANTEES_SAAS.DEFAULT },
    AX: { code: 'AX', name: 'Aland, Îles', legalGuarantees: LEGAL_GUARANTEES.TBD, legalGuaranteesSaas: LEGAL_GUARANTEES_SAAS.DEFAULT },
    AL: { code: 'AL', name: 'Albanie', legalGuarantees: LEGAL_GUARANTEES.TBD, legalGuaranteesSaas: LEGAL_GUARANTEES_SAAS.DEFAULT },
    DZ: { code: 'DZ', name: 'Algérie', legalGuarantees: LEGAL_GUARANTEES.TBD, legalGuaranteesSaas: LEGAL_GUARANTEES_SAAS.DEFAULT },
    DE: { code: 'DE', name: 'Allemagne', legalGuarantees: LEGAL_GUARANTEES.NA, legalGuaranteesSaas: LEGAL_GUARANTEES_SAAS.DEFAULT },
    AD: { code: 'AD', name: 'Andorre', legalGuarantees: LEGAL_GUARANTEES.OK, legalGuaranteesSaas: LEGAL_GUARANTEES_SAAS.DEFAULT },
    AO: { code: 'AO', name: 'Angola', legalGuarantees: LEGAL_GUARANTEES.TBD, legalGuaranteesSaas: LEGAL_GUARANTEES_SAAS.DEFAULT },
    AI: { code: 'AI', name: 'Anguilla', legalGuarantees: LEGAL_GUARANTEES.TBD, legalGuaranteesSaas: LEGAL_GUARANTEES_SAAS.DEFAULT },
    AQ: { code: 'AQ', name: 'Antarctique', legalGuarantees: LEGAL_GUARANTEES.TBD, legalGuaranteesSaas: LEGAL_GUARANTEES_SAAS.DEFAULT },
    AG: { code: 'AG', name: 'Antigua-Et-Barbuda', legalGuarantees: LEGAL_GUARANTEES.TBD, legalGuaranteesSaas: LEGAL_GUARANTEES_SAAS.DEFAULT },
    SA: { code: 'SA', name: 'Arabie Saoudite', legalGuarantees: LEGAL_GUARANTEES.TBD, legalGuaranteesSaas: LEGAL_GUARANTEES_SAAS.DEFAULT },
    AR: { code: 'AR', name: 'Argentine', legalGuarantees: LEGAL_GUARANTEES.OK, legalGuaranteesSaas: LEGAL_GUARANTEES_SAAS.DEFAULT },
    AM: { code: 'AM', name: 'Arménie', legalGuarantees: LEGAL_GUARANTEES.TBD, legalGuaranteesSaas: LEGAL_GUARANTEES_SAAS.DEFAULT },
    AW: { code: 'AW', name: 'Aruba', legalGuarantees: LEGAL_GUARANTEES.TBD, legalGuaranteesSaas: LEGAL_GUARANTEES_SAAS.DEFAULT },
    AU: { code: 'AU', name: 'Australie', legalGuarantees: LEGAL_GUARANTEES.TBD, legalGuaranteesSaas: LEGAL_GUARANTEES_SAAS.DEFAULT },
    AT: { code: 'AT', name: 'Autriche', legalGuarantees: LEGAL_GUARANTEES.NA, legalGuaranteesSaas: LEGAL_GUARANTEES_SAAS.DEFAULT },
    AZ: { code: 'AZ', name: 'Azerbaïdjan', legalGuarantees: LEGAL_GUARANTEES.TBD, legalGuaranteesSaas: LEGAL_GUARANTEES_SAAS.DEFAULT },
    BS: { code: 'BS', name: 'Bahamas', legalGuarantees: LEGAL_GUARANTEES.TBD, legalGuaranteesSaas: LEGAL_GUARANTEES_SAAS.DEFAULT },
    BH: { code: 'BH', name: 'Bahreïn', legalGuarantees: LEGAL_GUARANTEES.TBD, legalGuaranteesSaas: LEGAL_GUARANTEES_SAAS.DEFAULT },
    BD: { code: 'BD', name: 'Bangladesh', legalGuarantees: LEGAL_GUARANTEES.TBD, legalGuaranteesSaas: LEGAL_GUARANTEES_SAAS.DEFAULT },
    BB: { code: 'BB', name: 'Barbade', legalGuarantees: LEGAL_GUARANTEES.TBD, legalGuaranteesSaas: LEGAL_GUARANTEES_SAAS.DEFAULT },
    BY: { code: 'BY', name: 'Bélarus', legalGuarantees: LEGAL_GUARANTEES.TBD, legalGuaranteesSaas: LEGAL_GUARANTEES_SAAS.DEFAULT },
    BE: { code: 'BE', name: 'Belgique', legalGuarantees: LEGAL_GUARANTEES.NA, legalGuaranteesSaas: LEGAL_GUARANTEES_SAAS.DEFAULT },
    BZ: { code: 'BZ', name: 'Belize', legalGuarantees: LEGAL_GUARANTEES.TBD, legalGuaranteesSaas: LEGAL_GUARANTEES_SAAS.DEFAULT },
    BJ: { code: 'BJ', name: 'Bénin', legalGuarantees: LEGAL_GUARANTEES.TBD, legalGuaranteesSaas: LEGAL_GUARANTEES_SAAS.DEFAULT },
    BM: { code: 'BM', name: 'Bermudes', legalGuarantees: LEGAL_GUARANTEES.TBD, legalGuaranteesSaas: LEGAL_GUARANTEES_SAAS.DEFAULT },
    BT: { code: 'BT', name: 'Bhoutan', legalGuarantees: LEGAL_GUARANTEES.TBD, legalGuaranteesSaas: LEGAL_GUARANTEES_SAAS.DEFAULT },
    BO: { code: 'BO', name: 'Bolivie, L\'état Plurinational De', legalGuarantees: LEGAL_GUARANTEES.TBD, legalGuaranteesSaas: LEGAL_GUARANTEES_SAAS.DEFAULT },
    BQ: { code: 'BQ', name: 'Bonaire, Saint-Eustache Et Saba', legalGuarantees: LEGAL_GUARANTEES.TBD, legalGuaranteesSaas: LEGAL_GUARANTEES_SAAS.DEFAULT },
    BA: { code: 'BA', name: 'Bosnie-Herzégovine', legalGuarantees: LEGAL_GUARANTEES.TBD, legalGuaranteesSaas: LEGAL_GUARANTEES_SAAS.DEFAULT },
    BW: { code: 'BW', name: 'Botswana', legalGuarantees: LEGAL_GUARANTEES.TBD, legalGuaranteesSaas: LEGAL_GUARANTEES_SAAS.DEFAULT },
    BV: { code: 'BV', name: 'Bouvet, Île', legalGuarantees: LEGAL_GUARANTEES.TBD, legalGuaranteesSaas: LEGAL_GUARANTEES_SAAS.DEFAULT },
    BR: { code: 'BR', name: 'Brésil', legalGuarantees: LEGAL_GUARANTEES.EU, legalGuaranteesSaas: LEGAL_GUARANTEES_SAAS.DEFAULT },
    BN: { code: 'BN', name: 'Brunei Darussalam', legalGuarantees: LEGAL_GUARANTEES.TBD, legalGuaranteesSaas: LEGAL_GUARANTEES_SAAS.DEFAULT },
    BG: { code: 'BG', name: 'Bulgarie', legalGuarantees: LEGAL_GUARANTEES.NA, legalGuaranteesSaas: LEGAL_GUARANTEES_SAAS.DEFAULT },
    BF: { code: 'BF', name: 'Burkina Faso', legalGuarantees: LEGAL_GUARANTEES.TBD, legalGuaranteesSaas: LEGAL_GUARANTEES_SAAS.DEFAULT },
    BI: { code: 'BI', name: 'Burundi', legalGuarantees: LEGAL_GUARANTEES.TBD, legalGuaranteesSaas: LEGAL_GUARANTEES_SAAS.DEFAULT },
    KY: { code: 'KY', name: 'Caïmans, Îles', legalGuarantees: LEGAL_GUARANTEES.TBD, legalGuaranteesSaas: LEGAL_GUARANTEES_SAAS.DEFAULT },
    KH: { code: 'KH', name: 'Cambodge', legalGuarantees: LEGAL_GUARANTEES.TBD, legalGuaranteesSaas: LEGAL_GUARANTEES_SAAS.DEFAULT },
    CM: { code: 'CM', name: 'Cameroun', legalGuarantees: LEGAL_GUARANTEES.TBD, legalGuaranteesSaas: LEGAL_GUARANTEES_SAAS.DEFAULT },
    CA: { code: 'CA', name: 'Canada', legalGuarantees: LEGAL_GUARANTEES.OK, legalGuaranteesSaas: LEGAL_GUARANTEES_SAAS.DEFAULT },
    CV: { code: 'CV', name: 'Cap-Vert', legalGuarantees: LEGAL_GUARANTEES.TBD, legalGuaranteesSaas: LEGAL_GUARANTEES_SAAS.DEFAULT },
    CF: { code: 'CF', name: 'Centrafricaine, République', legalGuarantees: LEGAL_GUARANTEES.TBD, legalGuaranteesSaas: LEGAL_GUARANTEES_SAAS.DEFAULT },
    CL: { code: 'CL', name: 'Chili', legalGuarantees: LEGAL_GUARANTEES.TBD, legalGuaranteesSaas: LEGAL_GUARANTEES_SAAS.DEFAULT },
    CN: { code: 'CN', name: 'Chine', legalGuarantees: LEGAL_GUARANTEES.TBD, legalGuaranteesSaas: LEGAL_GUARANTEES_SAAS.DEFAULT },
    CX: { code: 'CX', name: 'Christmas, Île', legalGuarantees: LEGAL_GUARANTEES.TBD, legalGuaranteesSaas: LEGAL_GUARANTEES_SAAS.DEFAULT },
    CY: { code: 'CY', name: 'Chypre', legalGuarantees: LEGAL_GUARANTEES.NA, legalGuaranteesSaas: LEGAL_GUARANTEES_SAAS.DEFAULT },
    CC: { code: 'CC', name: 'Cocos (Keeling), Îles', legalGuarantees: LEGAL_GUARANTEES.TBD, legalGuaranteesSaas: LEGAL_GUARANTEES_SAAS.DEFAULT },
    CO: { code: 'CO', name: 'Colombie', legalGuarantees: LEGAL_GUARANTEES.TBD, legalGuaranteesSaas: LEGAL_GUARANTEES_SAAS.DEFAULT },
    KM: { code: 'KM', name: 'Comores', legalGuarantees: LEGAL_GUARANTEES.TBD, legalGuaranteesSaas: LEGAL_GUARANTEES_SAAS.DEFAULT },
    CG: { code: 'CG', name: 'Congo', legalGuarantees: LEGAL_GUARANTEES.TBD, legalGuaranteesSaas: LEGAL_GUARANTEES_SAAS.DEFAULT },
    CD: { code: 'CD', name: 'Congo, La République Démocratique Du', legalGuarantees: LEGAL_GUARANTEES.TBD, legalGuaranteesSaas: LEGAL_GUARANTEES_SAAS.DEFAULT },
    CK: { code: 'CK', name: 'Cook, Îles', legalGuarantees: LEGAL_GUARANTEES.TBD, legalGuaranteesSaas: LEGAL_GUARANTEES_SAAS.DEFAULT },
    KR: { code: 'KR', name: 'Corée, République De', legalGuarantees: LEGAL_GUARANTEES.TBD, legalGuaranteesSaas: LEGAL_GUARANTEES_SAAS.DEFAULT },
    KP: { code: 'KP', name: 'Corée, République Populaire Démocratique De', legalGuarantees: LEGAL_GUARANTEES.TBD, legalGuaranteesSaas: LEGAL_GUARANTEES_SAAS.DEFAULT },
    CR: { code: 'CR', name: 'Costa Rica', legalGuarantees: LEGAL_GUARANTEES.TBD, legalGuaranteesSaas: LEGAL_GUARANTEES_SAAS.DEFAULT },
    CI: { code: 'CI', name: 'Côte D\'ivoire', legalGuarantees: LEGAL_GUARANTEES.TBD, legalGuaranteesSaas: LEGAL_GUARANTEES_SAAS.DEFAULT },
    HR: { code: 'HR', name: 'Croatie', legalGuarantees: LEGAL_GUARANTEES.NA, legalGuaranteesSaas: LEGAL_GUARANTEES_SAAS.DEFAULT },
    CU: { code: 'CU', name: 'Cuba', legalGuarantees: LEGAL_GUARANTEES.TBD, legalGuaranteesSaas: LEGAL_GUARANTEES_SAAS.DEFAULT },
    CW: { code: 'CW', name: 'Curaçao', legalGuarantees: LEGAL_GUARANTEES.TBD, legalGuaranteesSaas: LEGAL_GUARANTEES_SAAS.DEFAULT },
    DK: { code: 'DK', name: 'Danemark', legalGuarantees: LEGAL_GUARANTEES.NA, legalGuaranteesSaas: LEGAL_GUARANTEES_SAAS.DEFAULT },
    DJ: { code: 'DJ', name: 'Djibouti', legalGuarantees: LEGAL_GUARANTEES.TBD, legalGuaranteesSaas: LEGAL_GUARANTEES_SAAS.DEFAULT },
    DO: { code: 'DO', name: 'Dominicaine, République', legalGuarantees: LEGAL_GUARANTEES.TBD, legalGuaranteesSaas: LEGAL_GUARANTEES_SAAS.DEFAULT },
    DM: { code: 'DM', name: 'Dominique', legalGuarantees: LEGAL_GUARANTEES.TBD, legalGuaranteesSaas: LEGAL_GUARANTEES_SAAS.DEFAULT },
    EG: { code: 'EG', name: 'Egypte', legalGuarantees: LEGAL_GUARANTEES.TBD, legalGuaranteesSaas: LEGAL_GUARANTEES_SAAS.DEFAULT },
    SV: { code: 'SV', name: 'El Salvador', legalGuarantees: LEGAL_GUARANTEES.TBD, legalGuaranteesSaas: LEGAL_GUARANTEES_SAAS.DEFAULT },
    AE: { code: 'AE', name: 'Emirats Arabes Unis', legalGuarantees: LEGAL_GUARANTEES.TBD, legalGuaranteesSaas: LEGAL_GUARANTEES_SAAS.DEFAULT },
    EC: { code: 'EC', name: 'Equateur', legalGuarantees: LEGAL_GUARANTEES.TBD, legalGuaranteesSaas: LEGAL_GUARANTEES_SAAS.DEFAULT },
    ER: { code: 'ER', name: 'Erythrée', legalGuarantees: LEGAL_GUARANTEES.TBD, legalGuaranteesSaas: LEGAL_GUARANTEES_SAAS.DEFAULT },
    ES: { code: 'ES', name: 'Espagne', legalGuarantees: LEGAL_GUARANTEES.NA, legalGuaranteesSaas: LEGAL_GUARANTEES_SAAS.DEFAULT },
    EE: { code: 'EE', name: 'Estonie', legalGuarantees: LEGAL_GUARANTEES.NA, legalGuaranteesSaas: LEGAL_GUARANTEES_SAAS.DEFAULT },
    EU: { code: 'EU', name: 'Union Européenne', legalGuarantees: LEGAL_GUARANTEES.NA, legalGuaranteesSaas: LEGAL_GUARANTEES.NA },
    US: { code: 'US', name: 'Etats-Unis', legalGuarantees: LEGAL_GUARANTEES.NA, legalGuaranteesSaas: LEGAL_GUARANTEES_SAAS.EU_MODEL_CLAUSE },
    ET: { code: 'ET', name: 'Ethiopie', legalGuarantees: LEGAL_GUARANTEES.TBD, legalGuaranteesSaas: LEGAL_GUARANTEES_SAAS.DEFAULT },
    FK: { code: 'FK', name: 'Falkland, Îles (Malvinas)', legalGuarantees: LEGAL_GUARANTEES.TBD, legalGuaranteesSaas: LEGAL_GUARANTEES_SAAS.DEFAULT },
    FO: { code: 'FO', name: 'Féroé, Îles', legalGuarantees: LEGAL_GUARANTEES.OK, legalGuaranteesSaas: LEGAL_GUARANTEES_SAAS.DEFAULT },
    FJ: { code: 'FJ', name: 'Fidji', legalGuarantees: LEGAL_GUARANTEES.TBD, legalGuaranteesSaas: LEGAL_GUARANTEES_SAAS.DEFAULT },
    FI: { code: 'FI', name: 'Finlande', legalGuarantees: LEGAL_GUARANTEES.NA, legalGuaranteesSaas: LEGAL_GUARANTEES_SAAS.DEFAULT },
    FR: { code: 'FR', name: 'France', legalGuarantees: LEGAL_GUARANTEES.NA, legalGuaranteesSaas: LEGAL_GUARANTEES.NA },
    GA: { code: 'GA', name: 'Gabon', legalGuarantees: LEGAL_GUARANTEES.TBD, legalGuaranteesSaas: LEGAL_GUARANTEES_SAAS.DEFAULT },
    GM: { code: 'GM', name: 'Gambie', legalGuarantees: LEGAL_GUARANTEES.TBD, legalGuaranteesSaas: LEGAL_GUARANTEES_SAAS.DEFAULT },
    GE: { code: 'GE', name: 'Géorgie', legalGuarantees: LEGAL_GUARANTEES.TBD, legalGuaranteesSaas: LEGAL_GUARANTEES_SAAS.DEFAULT },
    GS: { code: 'GS', name: 'Géorgie Du Sud-Et-Les Îles Sandwich Du Sud', legalGuarantees: LEGAL_GUARANTEES.TBD, legalGuaranteesSaas: LEGAL_GUARANTEES_SAAS.DEFAULT },
    GH: { code: 'GH', name: 'Ghana', legalGuarantees: LEGAL_GUARANTEES.TBD, legalGuaranteesSaas: LEGAL_GUARANTEES_SAAS.DEFAULT },
    GI: { code: 'GI', name: 'Gibraltar', legalGuarantees: LEGAL_GUARANTEES.TBD, legalGuaranteesSaas: LEGAL_GUARANTEES_SAAS.DEFAULT },
    GR: { code: 'GR', name: 'Grèce', legalGuarantees: LEGAL_GUARANTEES.NA, legalGuaranteesSaas: LEGAL_GUARANTEES_SAAS.DEFAULT },
    GD: { code: 'GD', name: 'Grenade', legalGuarantees: LEGAL_GUARANTEES.TBD, legalGuaranteesSaas: LEGAL_GUARANTEES_SAAS.DEFAULT },
    GL: { code: 'GL', name: 'Groenland', legalGuarantees: LEGAL_GUARANTEES.TBD, legalGuaranteesSaas: LEGAL_GUARANTEES_SAAS.DEFAULT },
    GP: { code: 'GP', name: 'Guadeloupe', legalGuarantees: LEGAL_GUARANTEES.TBD, legalGuaranteesSaas: LEGAL_GUARANTEES_SAAS.DEFAULT },
    GU: { code: 'GU', name: 'Guam', legalGuarantees: LEGAL_GUARANTEES.TBD, legalGuaranteesSaas: LEGAL_GUARANTEES_SAAS.DEFAULT },
    GT: { code: 'GT', name: 'Guatemala', legalGuarantees: LEGAL_GUARANTEES.TBD, legalGuaranteesSaas: LEGAL_GUARANTEES_SAAS.DEFAULT },
    GG: { code: 'GG', name: 'Guernesey', legalGuarantees: LEGAL_GUARANTEES.OK, legalGuaranteesSaas: LEGAL_GUARANTEES_SAAS.DEFAULT },
    GN: { code: 'GN', name: 'Guinée', legalGuarantees: LEGAL_GUARANTEES.TBD, legalGuaranteesSaas: LEGAL_GUARANTEES_SAAS.DEFAULT },
    GW: { code: 'GW', name: 'Guinée-Bissau', legalGuarantees: LEGAL_GUARANTEES.TBD, legalGuaranteesSaas: LEGAL_GUARANTEES_SAAS.DEFAULT },
    GQ: { code: 'GQ', name: 'Guinée Équatoriale', legalGuarantees: LEGAL_GUARANTEES.TBD, legalGuaranteesSaas: LEGAL_GUARANTEES_SAAS.DEFAULT },
    GY: { code: 'GY', name: 'Guyana', legalGuarantees: LEGAL_GUARANTEES.TBD, legalGuaranteesSaas: LEGAL_GUARANTEES_SAAS.DEFAULT },
    GF: { code: 'GF', name: 'Guyane Française', legalGuarantees: LEGAL_GUARANTEES.TBD, legalGuaranteesSaas: LEGAL_GUARANTEES_SAAS.DEFAULT },
    HT: { code: 'HT', name: 'Haïti', legalGuarantees: LEGAL_GUARANTEES.TBD, legalGuaranteesSaas: LEGAL_GUARANTEES_SAAS.DEFAULT },
    HM: { code: 'HM', name: 'Heard-Et-Îles Macdonald, Île', legalGuarantees: LEGAL_GUARANTEES.TBD, legalGuaranteesSaas: LEGAL_GUARANTEES_SAAS.DEFAULT },
    HN: { code: 'HN', name: 'Honduras', legalGuarantees: LEGAL_GUARANTEES.TBD, legalGuaranteesSaas: LEGAL_GUARANTEES_SAAS.DEFAULT },
    HK: { code: 'HK', name: 'Hong Kong', legalGuarantees: LEGAL_GUARANTEES.TBD, legalGuaranteesSaas: LEGAL_GUARANTEES_SAAS.DEFAULT },
    HU: { code: 'HU', name: 'Hongrie', legalGuarantees: LEGAL_GUARANTEES.NA, legalGuaranteesSaas: LEGAL_GUARANTEES_SAAS.DEFAULT },
    IM: { code: 'IM', name: 'Ile De Man', legalGuarantees: LEGAL_GUARANTEES.OK, legalGuaranteesSaas: LEGAL_GUARANTEES_SAAS.DEFAULT },
    UM: { code: 'UM', name: 'Iles Mineures Éloignées Des États-Unis', legalGuarantees: LEGAL_GUARANTEES.TBD, legalGuaranteesSaas: LEGAL_GUARANTEES_SAAS.DEFAULT },
    VG: { code: 'VG', name: 'Iles Vierges Britanniques', legalGuarantees: LEGAL_GUARANTEES.TBD, legalGuaranteesSaas: LEGAL_GUARANTEES_SAAS.DEFAULT },
    VI: { code: 'VI', name: 'Iles Vierges Des États-Unis', legalGuarantees: LEGAL_GUARANTEES.TBD, legalGuaranteesSaas: LEGAL_GUARANTEES_SAAS.DEFAULT },
    IN: { code: 'IN', name: 'Inde', legalGuarantees: LEGAL_GUARANTEES.EU, legalGuaranteesSaas: LEGAL_GUARANTEES_SAAS.DEFAULT },
    ID: { code: 'ID', name: 'Indonésie', legalGuarantees: LEGAL_GUARANTEES.TBD, legalGuaranteesSaas: LEGAL_GUARANTEES_SAAS.DEFAULT },
    IR: { code: 'IR', name: 'Iran, République Islamique D\'', legalGuarantees: LEGAL_GUARANTEES.TBD, legalGuaranteesSaas: LEGAL_GUARANTEES_SAAS.DEFAULT },
    IQ: { code: 'IQ', name: 'Iraq', legalGuarantees: LEGAL_GUARANTEES.TBD, legalGuaranteesSaas: LEGAL_GUARANTEES_SAAS.DEFAULT },
    IE: { code: 'IE', name: 'Irlande', legalGuarantees: LEGAL_GUARANTEES.NA, legalGuaranteesSaas: LEGAL_GUARANTEES_SAAS.DEFAULT },
    IS: { code: 'IS', name: 'Islande', legalGuarantees: LEGAL_GUARANTEES.TBD, legalGuaranteesSaas: LEGAL_GUARANTEES_SAAS.DEFAULT },
    IL: { code: 'IL', name: 'Israël', legalGuarantees: LEGAL_GUARANTEES.OK, legalGuaranteesSaas: LEGAL_GUARANTEES_SAAS.DEFAULT },
    IT: { code: 'IT', name: 'Italie', legalGuarantees: LEGAL_GUARANTEES.NA, legalGuaranteesSaas: LEGAL_GUARANTEES_SAAS.DEFAULT },
    JM: { code: 'JM', name: 'Jamaïque', legalGuarantees: LEGAL_GUARANTEES.TBD, legalGuaranteesSaas: LEGAL_GUARANTEES_SAAS.DEFAULT },
    JP: { code: 'JP', name: 'Japon', legalGuarantees: LEGAL_GUARANTEES.TBD, legalGuaranteesSaas: LEGAL_GUARANTEES_SAAS.DEFAULT },
    JE: { code: 'JE', name: 'Jersey', legalGuarantees: LEGAL_GUARANTEES.OK, legalGuaranteesSaas: LEGAL_GUARANTEES_SAAS.DEFAULT },
    JO: { code: 'JO', name: 'Jordanie', legalGuarantees: LEGAL_GUARANTEES.TBD, legalGuaranteesSaas: LEGAL_GUARANTEES_SAAS.DEFAULT },
    KZ: { code: 'KZ', name: 'Kazakhstan', legalGuarantees: LEGAL_GUARANTEES.TBD, legalGuaranteesSaas: LEGAL_GUARANTEES_SAAS.DEFAULT },
    KE: { code: 'KE', name: 'Kenya', legalGuarantees: LEGAL_GUARANTEES.TBD, legalGuaranteesSaas: LEGAL_GUARANTEES_SAAS.DEFAULT },
    KG: { code: 'KG', name: 'Kirghizistan', legalGuarantees: LEGAL_GUARANTEES.TBD, legalGuaranteesSaas: LEGAL_GUARANTEES_SAAS.DEFAULT },
    KI: { code: 'KI', name: 'Kiribati', legalGuarantees: LEGAL_GUARANTEES.TBD, legalGuaranteesSaas: LEGAL_GUARANTEES_SAAS.DEFAULT },
    KW: { code: 'KW', name: 'Koweït', legalGuarantees: LEGAL_GUARANTEES.TBD, legalGuaranteesSaas: LEGAL_GUARANTEES_SAAS.DEFAULT },
    LA: { code: 'LA', name: 'Lao, République Démocratique Populaire', legalGuarantees: LEGAL_GUARANTEES.TBD, legalGuaranteesSaas: LEGAL_GUARANTEES_SAAS.DEFAULT },
    LS: { code: 'LS', name: 'Lesotho', legalGuarantees: LEGAL_GUARANTEES.TBD, legalGuaranteesSaas: LEGAL_GUARANTEES_SAAS.DEFAULT },
    LV: { code: 'LV', name: 'Lettonie', legalGuarantees: LEGAL_GUARANTEES.NA, legalGuaranteesSaas: LEGAL_GUARANTEES_SAAS.DEFAULT },
    LB: { code: 'LB', name: 'Liban', legalGuarantees: LEGAL_GUARANTEES.TBD, legalGuaranteesSaas: LEGAL_GUARANTEES_SAAS.DEFAULT },
    LR: { code: 'LR', name: 'Libéria', legalGuarantees: LEGAL_GUARANTEES.TBD, legalGuaranteesSaas: LEGAL_GUARANTEES_SAAS.DEFAULT },
    LY: { code: 'LY', name: 'Libye', legalGuarantees: LEGAL_GUARANTEES.TBD, legalGuaranteesSaas: LEGAL_GUARANTEES_SAAS.DEFAULT },
    LI: { code: 'LI', name: 'Liechtenstein', legalGuarantees: LEGAL_GUARANTEES.TBD, legalGuaranteesSaas: LEGAL_GUARANTEES_SAAS.DEFAULT },
    LT: { code: 'LT', name: 'Lituanie', legalGuarantees: LEGAL_GUARANTEES.NA, legalGuaranteesSaas: LEGAL_GUARANTEES_SAAS.DEFAULT },
    LU: { code: 'LU', name: 'Luxembourg', legalGuarantees: LEGAL_GUARANTEES.NA, legalGuaranteesSaas: LEGAL_GUARANTEES_SAAS.DEFAULT },
    MO: { code: 'MO', name: 'Macao', legalGuarantees: LEGAL_GUARANTEES.TBD, legalGuaranteesSaas: LEGAL_GUARANTEES_SAAS.DEFAULT },
    MK: { code: 'MK', name: 'Macédoine, L\'ex-République Yougoslave De', legalGuarantees: LEGAL_GUARANTEES.TBD, legalGuaranteesSaas: LEGAL_GUARANTEES_SAAS.DEFAULT },
    MG: { code: 'MG', name: 'Madagascar', legalGuarantees: LEGAL_GUARANTEES.TBD, legalGuaranteesSaas: LEGAL_GUARANTEES_SAAS.DEFAULT },
    MY: { code: 'MY', name: 'Malaisie', legalGuarantees: LEGAL_GUARANTEES.TBD, legalGuaranteesSaas: LEGAL_GUARANTEES_SAAS.DEFAULT },
    MW: { code: 'MW', name: 'Malawi', legalGuarantees: LEGAL_GUARANTEES.TBD, legalGuaranteesSaas: LEGAL_GUARANTEES_SAAS.DEFAULT },
    MV: { code: 'MV', name: 'Maldives', legalGuarantees: LEGAL_GUARANTEES.TBD, legalGuaranteesSaas: LEGAL_GUARANTEES_SAAS.DEFAULT },
    ML: { code: 'ML', name: 'Mali', legalGuarantees: LEGAL_GUARANTEES.TBD, legalGuaranteesSaas: LEGAL_GUARANTEES_SAAS.DEFAULT },
    MT: { code: 'MT', name: 'Malte', legalGuarantees: LEGAL_GUARANTEES.NA, legalGuaranteesSaas: LEGAL_GUARANTEES_SAAS.DEFAULT },
    MP: { code: 'MP', name: 'Mariannes Du Nord, Îles', legalGuarantees: LEGAL_GUARANTEES.TBD, legalGuaranteesSaas: LEGAL_GUARANTEES_SAAS.DEFAULT },
    MA: { code: 'MA', name: 'Maroc', legalGuarantees: LEGAL_GUARANTEES.TBD, legalGuaranteesSaas: LEGAL_GUARANTEES_SAAS.DEFAULT },
    MH: { code: 'MH', name: 'Marshall, Îles', legalGuarantees: LEGAL_GUARANTEES.TBD, legalGuaranteesSaas: LEGAL_GUARANTEES_SAAS.DEFAULT },
    MQ: { code: 'MQ', name: 'Martinique', legalGuarantees: LEGAL_GUARANTEES.TBD, legalGuaranteesSaas: LEGAL_GUARANTEES_SAAS.DEFAULT },
    MU: { code: 'MU', name: 'Maurice', legalGuarantees: LEGAL_GUARANTEES.TBD, legalGuaranteesSaas: LEGAL_GUARANTEES_SAAS.DEFAULT },
    MR: { code: 'MR', name: 'Mauritanie', legalGuarantees: LEGAL_GUARANTEES.TBD, legalGuaranteesSaas: LEGAL_GUARANTEES_SAAS.DEFAULT },
    YT: { code: 'YT', name: 'Mayotte', legalGuarantees: LEGAL_GUARANTEES.TBD, legalGuaranteesSaas: LEGAL_GUARANTEES_SAAS.DEFAULT },
    MX: { code: 'MX', name: 'Mexique', legalGuarantees: LEGAL_GUARANTEES.TBD, legalGuaranteesSaas: LEGAL_GUARANTEES_SAAS.DEFAULT },
    FM: { code: 'FM', name: 'Micronésie, États Fédérés De', legalGuarantees: LEGAL_GUARANTEES.TBD, legalGuaranteesSaas: LEGAL_GUARANTEES_SAAS.DEFAULT },
    MD: { code: 'MD', name: 'Moldova, République De', legalGuarantees: LEGAL_GUARANTEES.TBD, legalGuaranteesSaas: LEGAL_GUARANTEES_SAAS.DEFAULT },
    MC: { code: 'MC', name: 'Monaco', legalGuarantees: LEGAL_GUARANTEES.TBD, legalGuaranteesSaas: LEGAL_GUARANTEES_SAAS.DEFAULT },
    MN: { code: 'MN', name: 'Mongolie', legalGuarantees: LEGAL_GUARANTEES.TBD, legalGuaranteesSaas: LEGAL_GUARANTEES_SAAS.DEFAULT },
    ME: { code: 'ME', name: 'Monténégro', legalGuarantees: LEGAL_GUARANTEES.TBD, legalGuaranteesSaas: LEGAL_GUARANTEES_SAAS.DEFAULT },
    MS: { code: 'MS', name: 'Montserrat', legalGuarantees: LEGAL_GUARANTEES.TBD, legalGuaranteesSaas: LEGAL_GUARANTEES_SAAS.DEFAULT },
    MZ: { code: 'MZ', name: 'Mozambique', legalGuarantees: LEGAL_GUARANTEES.TBD, legalGuaranteesSaas: LEGAL_GUARANTEES_SAAS.DEFAULT },
    MM: { code: 'MM', name: 'Myanmar', legalGuarantees: LEGAL_GUARANTEES.TBD, legalGuaranteesSaas: LEGAL_GUARANTEES_SAAS.DEFAULT },
    NA: { code: 'NA', name: 'Namibie', legalGuarantees: LEGAL_GUARANTEES.TBD, legalGuaranteesSaas: LEGAL_GUARANTEES_SAAS.DEFAULT },
    NR: { code: 'NR', name: 'Nauru', legalGuarantees: LEGAL_GUARANTEES.TBD, legalGuaranteesSaas: LEGAL_GUARANTEES_SAAS.DEFAULT },
    NP: { code: 'NP', name: 'Népal', legalGuarantees: LEGAL_GUARANTEES.TBD, legalGuaranteesSaas: LEGAL_GUARANTEES_SAAS.DEFAULT },
    NI: { code: 'NI', name: 'Nicaragua', legalGuarantees: LEGAL_GUARANTEES.TBD, legalGuaranteesSaas: LEGAL_GUARANTEES_SAAS.DEFAULT },
    NE: { code: 'NE', name: 'Niger', legalGuarantees: LEGAL_GUARANTEES.TBD, legalGuaranteesSaas: LEGAL_GUARANTEES_SAAS.DEFAULT },
    NG: { code: 'NG', name: 'Nigéria', legalGuarantees: LEGAL_GUARANTEES.TBD, legalGuaranteesSaas: LEGAL_GUARANTEES_SAAS.DEFAULT },
    NU: { code: 'NU', name: 'Niué', legalGuarantees: LEGAL_GUARANTEES.TBD, legalGuaranteesSaas: LEGAL_GUARANTEES_SAAS.DEFAULT },
    NF: { code: 'NF', name: 'Norfolk, Île', legalGuarantees: LEGAL_GUARANTEES.TBD, legalGuaranteesSaas: LEGAL_GUARANTEES_SAAS.DEFAULT },
    NO: { code: 'NO', name: 'Norvège', legalGuarantees: LEGAL_GUARANTEES.TBD, legalGuaranteesSaas: LEGAL_GUARANTEES_SAAS.DEFAULT },
    NC: { code: 'NC', name: 'Nouvelle-Calédonie', legalGuarantees: LEGAL_GUARANTEES.TBD, legalGuaranteesSaas: LEGAL_GUARANTEES_SAAS.DEFAULT },
    NZ: { code: 'NZ', name: 'Nouvelle-Zélande', legalGuarantees: LEGAL_GUARANTEES.OK, legalGuaranteesSaas: LEGAL_GUARANTEES_SAAS.DEFAULT },
    IO: { code: 'IO', name: 'Océan Indien, Territoire Britannique De L\'', legalGuarantees: LEGAL_GUARANTEES.TBD, legalGuaranteesSaas: LEGAL_GUARANTEES_SAAS.DEFAULT },
    OM: { code: 'OM', name: 'Oman', legalGuarantees: LEGAL_GUARANTEES.TBD, legalGuaranteesSaas: LEGAL_GUARANTEES_SAAS.DEFAULT },
    UG: { code: 'UG', name: 'Ouganda', legalGuarantees: LEGAL_GUARANTEES.TBD, legalGuaranteesSaas: LEGAL_GUARANTEES_SAAS.DEFAULT },
    UZ: { code: 'UZ', name: 'Ouzbékistan', legalGuarantees: LEGAL_GUARANTEES.TBD, legalGuaranteesSaas: LEGAL_GUARANTEES_SAAS.DEFAULT },
    PK: { code: 'PK', name: 'Pakistan', legalGuarantees: LEGAL_GUARANTEES.TBD, legalGuaranteesSaas: LEGAL_GUARANTEES_SAAS.DEFAULT },
    PW: { code: 'PW', name: 'Palaos', legalGuarantees: LEGAL_GUARANTEES.TBD, legalGuaranteesSaas: LEGAL_GUARANTEES_SAAS.DEFAULT },
    PS: { code: 'PS', name: 'Palestinien Occupé, Territoire', legalGuarantees: LEGAL_GUARANTEES.TBD, legalGuaranteesSaas: LEGAL_GUARANTEES_SAAS.DEFAULT },
    PA: { code: 'PA', name: 'Panama', legalGuarantees: LEGAL_GUARANTEES.TBD, legalGuaranteesSaas: LEGAL_GUARANTEES_SAAS.DEFAULT },
    PG: { code: 'PG', name: 'Papouasie-Nouvelle-Guinée', legalGuarantees: LEGAL_GUARANTEES.TBD, legalGuaranteesSaas: LEGAL_GUARANTEES_SAAS.DEFAULT },
    PY: { code: 'PY', name: 'Paraguay', legalGuarantees: LEGAL_GUARANTEES.TBD, legalGuaranteesSaas: LEGAL_GUARANTEES_SAAS.DEFAULT },
    NL: { code: 'NL', name: 'Pays-Bas', legalGuarantees: LEGAL_GUARANTEES.NA, legalGuaranteesSaas: LEGAL_GUARANTEES_SAAS.DEFAULT },
    PE: { code: 'PE', name: 'Pérou', legalGuarantees: LEGAL_GUARANTEES.TBD, legalGuaranteesSaas: LEGAL_GUARANTEES_SAAS.DEFAULT },
    PH: { code: 'PH', name: 'Philippines', legalGuarantees: LEGAL_GUARANTEES.TBD, legalGuaranteesSaas: LEGAL_GUARANTEES_SAAS.DEFAULT },
    PN: { code: 'PN', name: 'Pitcairn', legalGuarantees: LEGAL_GUARANTEES.TBD, legalGuaranteesSaas: LEGAL_GUARANTEES_SAAS.DEFAULT },
    PL: { code: 'PL', name: 'Pologne', legalGuarantees: LEGAL_GUARANTEES.NA, legalGuaranteesSaas: LEGAL_GUARANTEES_SAAS.DEFAULT },
    PF: { code: 'PF', name: 'Polynésie Française', legalGuarantees: LEGAL_GUARANTEES.TBD, legalGuaranteesSaas: LEGAL_GUARANTEES_SAAS.DEFAULT },
    PR: { code: 'PR', name: 'Porto Rico', legalGuarantees: LEGAL_GUARANTEES.TBD, legalGuaranteesSaas: LEGAL_GUARANTEES_SAAS.DEFAULT },
    PT: { code: 'PT', name: 'Portugal', legalGuarantees: LEGAL_GUARANTEES.NA, legalGuaranteesSaas: LEGAL_GUARANTEES_SAAS.DEFAULT },
    QA: { code: 'QA', name: 'Qatar', legalGuarantees: LEGAL_GUARANTEES.TBD, legalGuaranteesSaas: LEGAL_GUARANTEES_SAAS.DEFAULT },
    RE: { code: 'RE', name: 'Réunion', legalGuarantees: LEGAL_GUARANTEES.TBD, legalGuaranteesSaas: LEGAL_GUARANTEES_SAAS.DEFAULT },
    RO: { code: 'RO', name: 'Roumanie', legalGuarantees: LEGAL_GUARANTEES.NA, legalGuaranteesSaas: LEGAL_GUARANTEES_SAAS.DEFAULT },
    GB: { code: 'GB', name: 'Royaume-Uni', legalGuarantees: LEGAL_GUARANTEES.OK, legalGuaranteesSaas: LEGAL_GUARANTEES.OK },
    RU: { code: 'RU', name: 'Russie, Fédération De', legalGuarantees: LEGAL_GUARANTEES.TBD, legalGuaranteesSaas: LEGAL_GUARANTEES_SAAS.DEFAULT },
    RW: { code: 'RW', name: 'Rwanda', legalGuarantees: LEGAL_GUARANTEES.TBD, legalGuaranteesSaas: LEGAL_GUARANTEES_SAAS.DEFAULT },
    EH: { code: 'EH', name: 'Sahara Occidental', legalGuarantees: LEGAL_GUARANTEES.TBD, legalGuaranteesSaas: LEGAL_GUARANTEES_SAAS.DEFAULT },
    BL: { code: 'BL', name: 'Saint-Barthélemy', legalGuarantees: LEGAL_GUARANTEES.TBD, legalGuaranteesSaas: LEGAL_GUARANTEES_SAAS.DEFAULT },
    SH: { code: 'SH', name: 'Sainte-Hélène, Ascension Et Tristan Da Cunha', legalGuarantees: LEGAL_GUARANTEES.TBD, legalGuaranteesSaas: LEGAL_GUARANTEES_SAAS.DEFAULT },
    LC: { code: 'LC', name: 'Sainte-Lucie', legalGuarantees: LEGAL_GUARANTEES.TBD, legalGuaranteesSaas: LEGAL_GUARANTEES_SAAS.DEFAULT },
    KN: { code: 'KN', name: 'Saint-Kitts-Et-Nevis', legalGuarantees: LEGAL_GUARANTEES.TBD, legalGuaranteesSaas: LEGAL_GUARANTEES_SAAS.DEFAULT },
    SM: { code: 'SM', name: 'Saint-Marin', legalGuarantees: LEGAL_GUARANTEES.TBD, legalGuaranteesSaas: LEGAL_GUARANTEES_SAAS.DEFAULT },
    MF: { code: 'MF', name: 'Saint-Martin(Partie Française)', legalGuarantees: LEGAL_GUARANTEES.TBD, legalGuaranteesSaas: LEGAL_GUARANTEES_SAAS.DEFAULT },
    SX: { code: 'SX', name: 'Saint-Martin (Partie Néerlandaise)', legalGuarantees: LEGAL_GUARANTEES.TBD, legalGuaranteesSaas: LEGAL_GUARANTEES_SAAS.DEFAULT },
    PM: { code: 'PM', name: 'Saint-Pierre-Et-Miquelon', legalGuarantees: LEGAL_GUARANTEES.TBD, legalGuaranteesSaas: LEGAL_GUARANTEES_SAAS.DEFAULT },
    VA: { code: 'VA', name: 'Saint-Siège (État De La Cité Du Vatican)', legalGuarantees: LEGAL_GUARANTEES.TBD, legalGuaranteesSaas: LEGAL_GUARANTEES_SAAS.DEFAULT },
    VC: { code: 'VC', name: 'Saint-Vincent-Et-Les Grenadines', legalGuarantees: LEGAL_GUARANTEES.TBD, legalGuaranteesSaas: LEGAL_GUARANTEES_SAAS.DEFAULT },
    SB: { code: 'SB', name: 'Salomon, Îles', legalGuarantees: LEGAL_GUARANTEES.TBD, legalGuaranteesSaas: LEGAL_GUARANTEES_SAAS.DEFAULT },
    WS: { code: 'WS', name: 'Samoa', legalGuarantees: LEGAL_GUARANTEES.TBD, legalGuaranteesSaas: LEGAL_GUARANTEES_SAAS.DEFAULT },
    AS: { code: 'AS', name: 'Samoa Américaines', legalGuarantees: LEGAL_GUARANTEES.TBD, legalGuaranteesSaas: LEGAL_GUARANTEES_SAAS.DEFAULT },
    ST: { code: 'ST', name: 'Sao Tomé-Et-Principe', legalGuarantees: LEGAL_GUARANTEES.TBD, legalGuaranteesSaas: LEGAL_GUARANTEES_SAAS.DEFAULT },
    SN: { code: 'SN', name: 'Sénégal', legalGuarantees: LEGAL_GUARANTEES.TBD, legalGuaranteesSaas: LEGAL_GUARANTEES_SAAS.DEFAULT },
    RS: { code: 'RS', name: 'Serbie', legalGuarantees: LEGAL_GUARANTEES.TBD, legalGuaranteesSaas: LEGAL_GUARANTEES_SAAS.DEFAULT },
    SC: { code: 'SC', name: 'Seychelles', legalGuarantees: LEGAL_GUARANTEES.TBD, legalGuaranteesSaas: LEGAL_GUARANTEES_SAAS.DEFAULT },
    SL: { code: 'SL', name: 'Sierra Leone', legalGuarantees: LEGAL_GUARANTEES.TBD, legalGuaranteesSaas: LEGAL_GUARANTEES_SAAS.DEFAULT },
    SG: { code: 'SG', name: 'Singapour', legalGuarantees: LEGAL_GUARANTEES.TBD, legalGuaranteesSaas: LEGAL_GUARANTEES_SAAS.DEFAULT },
    SK: { code: 'SK', name: 'Slovaquie', legalGuarantees: LEGAL_GUARANTEES.NA, legalGuaranteesSaas: LEGAL_GUARANTEES_SAAS.DEFAULT },
    SI: { code: 'SI', name: 'Slovénie', legalGuarantees: LEGAL_GUARANTEES.NA, legalGuaranteesSaas: LEGAL_GUARANTEES_SAAS.DEFAULT },
    SO: { code: 'SO', name: 'Somalie', legalGuarantees: LEGAL_GUARANTEES.TBD, legalGuaranteesSaas: LEGAL_GUARANTEES_SAAS.DEFAULT },
    SD: { code: 'SD', name: 'Soudan', legalGuarantees: LEGAL_GUARANTEES.TBD, legalGuaranteesSaas: LEGAL_GUARANTEES_SAAS.DEFAULT },
    SS: { code: 'SS', name: 'Soudan Du Sud', legalGuarantees: LEGAL_GUARANTEES.TBD, legalGuaranteesSaas: LEGAL_GUARANTEES_SAAS.DEFAULT },
    LK: { code: 'LK', name: 'Sri Lanka', legalGuarantees: LEGAL_GUARANTEES.TBD, legalGuaranteesSaas: LEGAL_GUARANTEES_SAAS.DEFAULT },
    SE: { code: 'SE', name: 'Suède', legalGuarantees: LEGAL_GUARANTEES.NA, legalGuaranteesSaas: LEGAL_GUARANTEES_SAAS.DEFAULT },
    CH: { code: 'CH', name: 'Suisse', legalGuarantees: LEGAL_GUARANTEES.OK, legalGuaranteesSaas: LEGAL_GUARANTEES_SAAS.DEFAULT },
    SR: { code: 'SR', name: 'Suriname', legalGuarantees: LEGAL_GUARANTEES.TBD, legalGuaranteesSaas: LEGAL_GUARANTEES_SAAS.DEFAULT },
    SJ: { code: 'SJ', name: 'Svalbard Et Île Jan Mayen', legalGuarantees: LEGAL_GUARANTEES.TBD, legalGuaranteesSaas: LEGAL_GUARANTEES_SAAS.DEFAULT },
    SZ: { code: 'SZ', name: 'Swaziland', legalGuarantees: LEGAL_GUARANTEES.TBD, legalGuaranteesSaas: LEGAL_GUARANTEES_SAAS.DEFAULT },
    SY: { code: 'SY', name: 'Syrienne, République Arabe', legalGuarantees: LEGAL_GUARANTEES.TBD, legalGuaranteesSaas: LEGAL_GUARANTEES_SAAS.DEFAULT },
    TJ: { code: 'TJ', name: 'Tadjikistan', legalGuarantees: LEGAL_GUARANTEES.TBD, legalGuaranteesSaas: LEGAL_GUARANTEES_SAAS.DEFAULT },
    TW: { code: 'TW', name: 'Taïwan, Province De Chine', legalGuarantees: LEGAL_GUARANTEES.TBD, legalGuaranteesSaas: LEGAL_GUARANTEES_SAAS.DEFAULT },
    TZ: { code: 'TZ', name: 'Tanzanie, République-Unie De', legalGuarantees: LEGAL_GUARANTEES.TBD, legalGuaranteesSaas: LEGAL_GUARANTEES_SAAS.DEFAULT },
    TD: { code: 'TD', name: 'Tchad', legalGuarantees: LEGAL_GUARANTEES.TBD, legalGuaranteesSaas: LEGAL_GUARANTEES_SAAS.DEFAULT },
    CZ: { code: 'CZ', name: 'Tchèque, République', legalGuarantees: LEGAL_GUARANTEES.NA, legalGuaranteesSaas: LEGAL_GUARANTEES_SAAS.DEFAULT },
    TF: { code: 'TF', name: 'Terres Australes Françaises', legalGuarantees: LEGAL_GUARANTEES.TBD, legalGuaranteesSaas: LEGAL_GUARANTEES_SAAS.DEFAULT },
    TH: { code: 'TH', name: 'Thaïlande', legalGuarantees: LEGAL_GUARANTEES.TBD, legalGuaranteesSaas: LEGAL_GUARANTEES_SAAS.DEFAULT },
    TL: { code: 'TL', name: 'Timor-Leste', legalGuarantees: LEGAL_GUARANTEES.TBD, legalGuaranteesSaas: LEGAL_GUARANTEES_SAAS.DEFAULT },
    TG: { code: 'TG', name: 'Togo', legalGuarantees: LEGAL_GUARANTEES.TBD, legalGuaranteesSaas: LEGAL_GUARANTEES_SAAS.DEFAULT },
    TK: { code: 'TK', name: 'Tokelau', legalGuarantees: LEGAL_GUARANTEES.TBD, legalGuaranteesSaas: LEGAL_GUARANTEES_SAAS.DEFAULT },
    TO: { code: 'TO', name: 'Tonga', legalGuarantees: LEGAL_GUARANTEES.TBD, legalGuaranteesSaas: LEGAL_GUARANTEES_SAAS.DEFAULT },
    TT: { code: 'TT', name: 'Trinité-Et-Tobago', legalGuarantees: LEGAL_GUARANTEES.TBD, legalGuaranteesSaas: LEGAL_GUARANTEES_SAAS.DEFAULT },
    TN: { code: 'TN', name: 'Tunisie', legalGuarantees: LEGAL_GUARANTEES.TBD, legalGuaranteesSaas: LEGAL_GUARANTEES_SAAS.DEFAULT },
    TM: { code: 'TM', name: 'Turkménistan', legalGuarantees: LEGAL_GUARANTEES.TBD, legalGuaranteesSaas: LEGAL_GUARANTEES_SAAS.DEFAULT },
    TC: { code: 'TC', name: 'Turks-Et-Caïcos, Îles', legalGuarantees: LEGAL_GUARANTEES.TBD, legalGuaranteesSaas: LEGAL_GUARANTEES_SAAS.DEFAULT },
    TR: { code: 'TR', name: 'Turquie', legalGuarantees: LEGAL_GUARANTEES.TBD, legalGuaranteesSaas: LEGAL_GUARANTEES_SAAS.DEFAULT },
    TV: { code: 'TV', name: 'Tuvalu', legalGuarantees: LEGAL_GUARANTEES.TBD, legalGuaranteesSaas: LEGAL_GUARANTEES_SAAS.DEFAULT },
    UA: { code: 'UA', name: 'Ukraine', legalGuarantees: LEGAL_GUARANTEES.TBD, legalGuaranteesSaas: LEGAL_GUARANTEES_SAAS.DEFAULT },
    UY: { code: 'UY', name: 'Uruguay', legalGuarantees: LEGAL_GUARANTEES.OK, legalGuaranteesSaas: LEGAL_GUARANTEES_SAAS.DEFAULT },
    VU: { code: 'VU', name: 'Vanuatu', legalGuarantees: LEGAL_GUARANTEES.TBD, legalGuaranteesSaas: LEGAL_GUARANTEES_SAAS.DEFAULT },
    VE: { code: 'VE', name: 'Venezuela, République Bolivarienne Du', legalGuarantees: LEGAL_GUARANTEES.TBD, legalGuaranteesSaas: LEGAL_GUARANTEES_SAAS.DEFAULT },
    VN: { code: 'VN', name: 'Viet Nam', legalGuarantees: LEGAL_GUARANTEES.TBD, legalGuaranteesSaas: LEGAL_GUARANTEES_SAAS.DEFAULT },
    WF: { code: 'WF', name: 'Wallis Et Futuna', legalGuarantees: LEGAL_GUARANTEES.TBD, legalGuaranteesSaas: LEGAL_GUARANTEES_SAAS.DEFAULT },
    YE: { code: 'YE', name: 'Yémen', legalGuarantees: LEGAL_GUARANTEES.TBD, legalGuaranteesSaas: LEGAL_GUARANTEES_SAAS.DEFAULT },
    ZM: { code: 'ZM', name: 'Zambie', legalGuarantees: LEGAL_GUARANTEES.TBD, legalGuaranteesSaas: LEGAL_GUARANTEES_SAAS.DEFAULT },
    ZW: { code: 'ZW', name: 'Zimbabwe', legalGuarantees: LEGAL_GUARANTEES.TBD, legalGuaranteesSaas: LEGAL_GUARANTEES_SAAS.DEFAULT },
    XX: { code: 'XX', name: 'Legacy code', legalGuarantees: LEGAL_GUARANTEES.TBD, legalGuaranteesSaas: LEGAL_GUARANTEES_SAAS.DEFAULT },
};
/* eslint-enable max-len */
